/**
 * /admin/social — Social Manager.
 *
 * Admin-gated. Pick a Centennial source (or paste custom text), pick channels
 * and tones, hit Generate, then push any variant straight into Buffer.
 *
 * Buffer publishing degrades gracefully if BUFFER_ACCESS_TOKEN isn't set —
 * the copy-to-clipboard fallback always works.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Wand2, Send, Copy, RefreshCcw, LogOut, Check, ExternalLink, CalendarClock, Image as ImageIcon, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import {
  fetchSocialSources,
  generateSocialVariants,
  fetchSocialChannels,
  publishSocialPost,
  fetchSocialHistory,
  buildSocialDigest,
} from "@/lib/api";
import { secureGet, secureSet, secureRemove } from "@/lib/secureStorage";
import { errorMessage } from "@/lib/errorMessage";
import { AutopilotCard } from "@/components/admin/AutopilotCard";
import { NewsletterCard } from "@/components/admin/NewsletterCard";
import useSEO from "@/lib/useSEO";
import Unlock from "@/components/admin/Unlock";

const PASS_KEY = "centennial_admin_pass_v1";

const CHANNEL_LABELS = {
  facebook: "Facebook",
  linkedin: "LinkedIn",
  pinterest: "Pinterest",
  shorts: "YouTube Shorts / TikTok",
};
const TONE_LABELS = { warm: "Warm", punchy: "Punchy" };

// Buffer's `service` values → the channel key our generator uses
const BUFFER_SERVICE_TO_CHANNEL = {
  facebook: "facebook",
  facebook_page: "facebook",
  facebook_group: "facebook",
  linkedin: "linkedin",
  linkedin_business: "linkedin",
  pinterest: "pinterest",
  tiktok: "shorts",
  youtube: "shorts",
};

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
      active
        ? "bg-[#1C2B23] text-white border-[#1C2B23]"
        : "bg-white text-[#5C6B62] border-[#E5E2DC] hover:border-[#2B5339] hover:text-[#2B5339]"
    }`}
  >
    {children}
  </button>
);

function VariantCard({ v, onPublish, onCopy }) {
  const [expanded, setExpanded] = useState(false);
  const label = CHANNEL_LABELS[v.channel] || v.channel;
  const tone = TONE_LABELS[v.tone] || v.tone;
  const preview = v.body.length > 220 && !expanded ? v.body.slice(0, 220) + "…" : v.body;
  return (
    <div
      data-testid={`variant-${v.channel}-${v.tone}`}
      className="bg-white border border-[#E5E2DC] rounded-xl p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs">
          <span className="font-medium text-[#1C2B23]">{label}</span>
          <span className="text-[#5C6B62]"> · {tone}</span>
        </div>
        <span className="text-[10px] text-[#5C6B62] font-mono-num">
          {v.char_count} chars
        </span>
      </div>
      {v.image_url && (
        <a
          href={v.image_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`variant-image-${v.channel}-${v.tone}`}
          className="block relative rounded-lg overflow-hidden border border-[#E5E2DC] group"
          title="Preview share card (opens full size)"
        >
          <img
            src={v.image_url}
            alt=""
            className="w-full h-auto object-cover"
            style={{ maxHeight: ["linkedin", "facebook"].includes(v.channel) ? 120 : 180 }}
          />
          <span className="absolute top-1 right-1 bg-black/40 text-white text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
            <ImageIcon size={9} /> {["linkedin", "facebook"].includes(v.channel) ? "1200×630" : "1080×1080"}
          </span>
        </a>
      )}
      <div
        className="text-sm text-[#1C2B23] whitespace-pre-wrap cursor-pointer"
        onClick={() => setExpanded((x) => !x)}
      >
        {preview}
      </div>
      {v.hashtags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {v.hashtags.map((h) => (
            <span key={h} className="text-[10px] text-[#2B5339] bg-[#E8F0EB] px-1.5 py-0.5 rounded-full">
              #{h}
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button
          data-testid={`copy-${v.channel}-${v.tone}`}
          onClick={() => onCopy(v)}
          className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full bg-[#F0EFEA] text-[#1C2B23] hover:bg-[#E5E2DC]"
        >
          <Copy size={11} /> Copy
        </button>
        <button
          data-testid={`publish-${v.channel}-${v.tone}`}
          onClick={() => onPublish(v)}
          className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full bg-[#2B5339] text-white hover:bg-[#1C2B23]"
        >
          <Send size={11} /> Push to Buffer
        </button>
      </div>
    </div>
  );
}

function PublishDialog({ open, onClose, variant, channels, onConfirm, publishing }) {
  const [channelId, setChannelId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  useEffect(() => {
    if (open && variant) {
      // Default to the first Buffer channel matching this variant's channel type
      const targetKey = variant.channel;
      const match = channels.find(
        (c) => BUFFER_SERVICE_TO_CHANNEL[c.service] === targetKey
      );
      setChannelId(match?.id || channels[0]?.id || "");
      setScheduledAt("");
    }
  }, [open, variant, channels]);

  if (!open || !variant) return null;
  const composed = `${variant.body}${
    variant.hashtags?.length ? "\n\n" + variant.hashtags.map((h) => `#${h}`).join(" ") : ""
  }`;

  return (
    <div
      data-testid="publish-dialog"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-medium text-[#1C2B23]">
          Push to Buffer · {CHANNEL_LABELS[variant.channel]}
        </h3>
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">Buffer profile</span>
          <select
            data-testid="publish-channel-select"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="mt-1 w-full bg-white border border-[#E5E2DC] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
          >
            {channels.length === 0 && <option value="">No Buffer channels — connect first</option>}
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.service}
                {c.paused ? " (queue paused)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-[#5C6B62] font-medium">
            Schedule (optional — leave blank for next queue slot)
          </span>
          <input
            data-testid="publish-schedule"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="mt-1 w-full bg-white border border-[#E5E2DC] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
          />
        </label>
        <div className="bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg p-3 max-h-40 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider text-[#5C6B62] mb-1">Preview</div>
          <div className="text-xs text-[#1C2B23] whitespace-pre-wrap">{composed}</div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-2 rounded-full bg-[#F0EFEA] text-[#5C6B62] hover:bg-[#E5E2DC]"
          >
            Cancel
          </button>
          <button
            data-testid="publish-confirm"
            type="button"
            disabled={!channelId || publishing}
            onClick={() =>
              onConfirm({
                channel_id: channelId,
                text: composed,
                scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
                channel_service:
                  channels.find((c) => c.id === channelId)?.service || variant.channel,
              })
            }
            className="inline-flex items-center gap-1 text-xs px-4 py-2 rounded-full bg-[#2B5339] text-white hover:bg-[#1C2B23] disabled:opacity-50"
          >
            {publishing && <Loader2 size={12} className="animate-spin" />}
            Push to Buffer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSocial() {
  useSEO({
    title: "Social Manager · Centennial",
    description: "Generate and push Centennial content to Buffer.",
    canonicalPath: "/admin/social",
  });

  const [pass, setPass] = useState(() => secureGet(PASS_KEY) || null);
  const [sources, setSources] = useState(null);
  const [channels, setChannels] = useState({ configured: false, channels: [] });
  const [history, setHistory] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);

  const [sourceSlug, setSourceSlug] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [customText, setCustomText] = useState("");
  const [headlineOverride, setHeadlineOverride] = useState("");
  const [subtitleOverride, setSubtitleOverride] = useState("");
  const [selChannels, setSelChannels] = useState(["facebook", "linkedin", "pinterest", "shorts"]);
  const [selTones, setSelTones] = useState(["warm", "punchy"]);
  const [generating, setGenerating] = useState(false);
  const [variants, setVariants] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [dialogVariant, setDialogVariant] = useState(null);

  // Weekly digest state
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestDrafts, setDigestDrafts] = useState([]);
  const [digestChecked, setDigestChecked] = useState({}); // keyed by draft.source.slug
  const [digestPushing, setDigestPushing] = useState(false);

  const load = async (p) => {
    try {
      const [s, c, h] = await Promise.all([
        fetchSocialSources(p),
        fetchSocialChannels(p),
        fetchSocialHistory(p),
      ]);
      setSources(s);
      setChannels(c);
      setHistory(h.items || []);
      setTotalClicks(h.total_clicks || 0);
    } catch (e) {
      if (e?.response?.status === 401) {
        secureRemove(PASS_KEY);
        setPass(null);
      } else {
        toast.error(errorMessage(e, "Failed to load."));
      }
    }
  };

  useEffect(() => {
    if (pass) load(pass);
  }, [pass]);

  const toggle = (arr, val, setter) =>
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const runGenerate = async () => {
    if (!sourceSlug && !customText.trim()) {
      toast.error("Pick a source or paste custom text.");
      return;
    }
    if (selChannels.length === 0 || selTones.length === 0) {
      toast.error("Pick at least one channel and one tone.");
      return;
    }
    setGenerating(true);
    setVariants([]);
    try {
      const res = await generateSocialVariants(pass, {
        source_slug: sourceSlug || null,
        custom_title: customTitle || null,
        custom_text: customText || null,
        channels: selChannels,
        tones: selTones,
        headline_override: headlineOverride || null,
        subtitle_override: subtitleOverride || null,
      });
      setVariants(res.variants || []);
      toast.success(`Generated ${res.variants?.length || 0} variants.`);
    } catch (e) {
      toast.error(errorMessage(e, "Generation failed."));
    } finally {
      setGenerating(false);
    }
  };

  const copyVariant = async (v) => {
    const text = `${v.body}${
      v.hashtags?.length ? "\n\n" + v.hashtags.map((h) => `#${h}`).join(" ") : ""
    }`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const openPublish = (v) => {
    if (!channels.configured) {
      toast.error("BUFFER_ACCESS_TOKEN not set — paste your token into backend/.env first.");
      return;
    }
    setDialogVariant(v);
  };

  const confirmPublish = async (payload) => {
    setPublishing(true);
    try {
      const res = await publishSocialPost(pass, {
        ...payload,
        source_slug: sourceSlug || null,
        image_url: dialogVariant?.image_url || null,
      });
      toast.success(`Queued in Buffer${res.post?.due_at ? " for " + new Date(res.post.due_at).toLocaleString() : ""}.`);
      setDialogVariant(null);
      const h = await fetchSocialHistory(pass);
      setHistory(h.items || []);
      setTotalClicks(h.total_clicks || 0);
    } catch (e) {
      toast.error(errorMessage(e, "Push failed."));
    } finally {
      setPublishing(false);
    }
  };

  const signOut = () => {
    secureRemove(PASS_KEY);
    setPass(null);
  };

  // ────────── Weekly digest handlers ──────────

  const runDigest = async () => {
    setDigestLoading(true);
    setDigestDrafts([]);
    setDigestChecked({});
    try {
      const res = await buildSocialDigest(pass, {
        lookback_days: 14,
        max_sources: 5,
        channel: "linkedin",
        tone: "warm",
        headline_override: headlineOverride || null,
        subtitle_override: subtitleOverride || null,
      });
      const drafts = res.drafts || [];
      setDigestDrafts(drafts);
      // Pre-check all drafts by default
      setDigestChecked(Object.fromEntries(drafts.map((d) => [d.source.slug, true])));
      if (drafts.length === 0) {
        toast.info(res.message || "Nothing stale to draft — everything was posted recently.");
      } else {
        toast.success(`Drafted ${drafts.length} posts for next week.`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Digest failed."));
    } finally {
      setDigestLoading(false);
    }
  };

  const updateDigestBody = (slug, body) => {
    setDigestDrafts((prev) =>
      prev.map((d) =>
        d.source.slug === slug
          ? { ...d, variant: { ...d.variant, body, char_count: body.length } }
          : d
      )
    );
  };

  const pushDigestApproved = async () => {
    if (!channels.configured) {
      toast.error("BUFFER_ACCESS_TOKEN not set — paste it into backend/.env first.");
      return;
    }
    const approved = digestDrafts.filter((d) => digestChecked[d.source.slug]);
    if (approved.length === 0) {
      toast.error("Check at least one draft to push.");
      return;
    }
    // Prefer a LinkedIn channel; fall back to the first connected channel.
    const target =
      (channels.channels || []).find(
        (c) => BUFFER_SERVICE_TO_CHANNEL[c.service] === "linkedin"
      ) || (channels.channels || [])[0];
    if (!target) {
      toast.error("No channels connected in Buffer.");
      return;
    }
    setDigestPushing(true);
    let ok = 0;
    for (const draft of approved) {
      try {
        const composed = `${draft.variant.body}${
          draft.variant.hashtags?.length
            ? "\n\n" + draft.variant.hashtags.map((h) => `#${h}`).join(" ")
            : ""
        }`;
        await publishSocialPost(pass, {
          channel_id: target.id,
          text: composed,
          scheduled_at: draft.suggested_at,
          source_slug: draft.source.slug,
          channel_service: target.service,
          image_url: draft.variant.image_url || null,
        });
        ok += 1;
      } catch (e) {
        toast.error(`${draft.source.title}: ${errorMessage(e, "push failed")}`);
      }
    }
    setDigestPushing(false);
    toast.success(`Pushed ${ok}/${approved.length} drafts to Buffer.`);
    if (ok > 0) {
      setDigestDrafts([]);
      setDigestChecked({});
      const h = await fetchSocialHistory(pass);
      setHistory(h.items || []);
      setTotalClicks(h.total_clicks || 0);
    }
  };

  if (!pass) return <Unlock passKey={PASS_KEY} onUnlocked={(p) => { secureSet(PASS_KEY, p); setPass(p); }} />;

  return (
    <div className="min-h-screen bg-[#F9F8F6] px-6 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Top nav */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339]"
          >
            <ArrowLeft size={14} /> Back to admin
          </Link>
          <div className="flex items-center gap-3">
            <button
              data-testid="social-refresh"
              onClick={() => load(pass)}
              className="inline-flex items-center gap-1.5 text-xs text-[#5C6B62] hover:text-[#2B5339]"
            >
              <RefreshCcw size={13} /> Refresh
            </button>
            <button
              data-testid="social-signout"
              onClick={signOut}
              className="inline-flex items-center gap-1.5 text-xs text-[#5C6B62] hover:text-[#C26747]"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>

        <h1 className="text-3xl font-medium text-[#1C2B23] mb-1">Social Manager</h1>
        <p className="text-sm text-[#5C6B62] mb-6">
          Spin Centennial content into channel-ready posts, then push straight into Buffer.
        </p>

        {!channels.configured && (
          <div
            data-testid="buffer-not-configured"
            className="bg-[#FDF6ED] border border-[#F0D9B4] text-[#8A6A28] rounded-xl p-4 text-xs mb-6"
          >
            <strong>Buffer not connected.</strong> Generation works, but &quot;Push to Buffer&quot; is
            disabled until you paste your Buffer access token into{" "}
            <code className="font-mono">backend/.env</code> as{" "}
            <code className="font-mono">BUFFER_ACCESS_TOKEN=…</code> and restart the backend.
            Get a token at{" "}
            <a
              className="underline"
              href="https://publish.buffer.com/settings/api"
              target="_blank"
              rel="noopener noreferrer"
            >
              publish.buffer.com/settings/api
            </a>
            .
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* ── Source ── */}
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-5 space-y-4">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              1. Pick a source
            </div>
            <label className="block">
              <span className="text-xs text-[#5C6B62] font-medium">Centennial page</span>
              <select
                data-testid="source-select"
                value={sourceSlug}
                onChange={(e) => setSourceSlug(e.target.value)}
                className="mt-1 w-full bg-white border border-[#E5E2DC] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
              >
                <option value="">— None (use custom text below) —</option>
                {(sources?.presets || []).map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.title}
                  </option>
                ))}
              </select>
            </label>
            <div className="text-[10px] text-[#5C6B62] text-center">— or —</div>
            <label className="block">
              <span className="text-xs text-[#5C6B62] font-medium">Custom title (optional)</span>
              <input
                data-testid="source-title"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder="e.g. New research on annuity ladders"
                className="mt-1 w-full bg-white border border-[#E5E2DC] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
              />
            </label>
            <label className="block">
              <span className="text-xs text-[#5C6B62] font-medium">Paste any article, blog, or notes</span>
              <textarea
                data-testid="source-text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                rows={6}
                placeholder="Paste raw text here and Centennial will spin it."
                className="mt-1 w-full bg-white border border-[#E5E2DC] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#2B5339] resize-y"
              />
            </label>

            <div className="border-t border-[#F0EFEA] pt-3 space-y-2">
              <div className="text-[10px] text-[#5C6B62] font-medium uppercase tracking-wider">
                Image overlay (optional)
              </div>
              <label className="block">
                <span className="text-xs text-[#5C6B62] font-medium">Custom headline</span>
                <input
                  data-testid="headline-override"
                  value={headlineOverride}
                  onChange={(e) => setHeadlineOverride(e.target.value)}
                  placeholder="e.g. New 2026 SECURE Act changes"
                  className="mt-1 w-full bg-white border border-[#E5E2DC] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
                />
              </label>
              <label className="block">
                <span className="text-xs text-[#5C6B62] font-medium">Custom subtitle</span>
                <input
                  data-testid="subtitle-override"
                  value={subtitleOverride}
                  onChange={(e) => setSubtitleOverride(e.target.value)}
                  placeholder="e.g. What it means for your plan"
                  className="mt-1 w-full bg-white border border-[#E5E2DC] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
                />
              </label>
              <p className="text-[10px] text-[#5C6B62] leading-relaxed">
                Overrides the source title on every generated share card — great for
                breaking-news posts. Leave blank to use the source&apos;s default title.
              </p>
            </div>
          </div>

          {/* ── Options ── */}
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-5 space-y-4">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              2. Pick channels &amp; tones
            </div>
            <div>
              <div className="text-xs text-[#5C6B62] font-medium mb-2">Channels</div>
              <div className="flex flex-wrap gap-2">
                {["facebook", "linkedin", "pinterest", "shorts"].map((c) => (
                  <Chip
                    key={c}
                    testid={`chip-channel-${c}`}
                    active={selChannels.includes(c)}
                    onClick={() => toggle(selChannels, c, setSelChannels)}
                  >
                    {CHANNEL_LABELS[c]}
                  </Chip>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-[#5C6B62] font-medium mb-2">Tones</div>
              <div className="flex flex-wrap gap-2">
                {["warm", "punchy"].map((t) => (
                  <Chip
                    key={t}
                    testid={`chip-tone-${t}`}
                    active={selTones.includes(t)}
                    onClick={() => toggle(selTones, t, setSelTones)}
                  >
                    {TONE_LABELS[t]}
                  </Chip>
                ))}
              </div>
            </div>
            <button
              data-testid="generate-btn"
              type="button"
              onClick={runGenerate}
              disabled={generating}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1C2B23] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#2B5339] disabled:opacity-50"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
              Generate {selChannels.length * selTones.length} variants
            </button>
            <p className="text-[11px] text-[#5C6B62] leading-relaxed">
              Uses Claude Sonnet via your Emergent LLM key. Each channel × tone combo produces one post.
            </p>
          </div>
        </div>

        {/* ── Weekly digest ── */}
        <div className="mt-6 bg-white border border-[#E5E2DC] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
                Weekly digest
              </div>
              <p className="text-xs text-[#5C6B62] mt-1">
                One LinkedIn post per source you haven&apos;t published in the last 14 days,
                pre-scheduled Mon–Fri at 9am UTC next week. Check what you like, edit inline,
                push all approved in one click.
              </p>
            </div>
            <button
              data-testid="digest-draft-btn"
              type="button"
              onClick={runDigest}
              disabled={digestLoading}
              className="inline-flex items-center gap-2 bg-[#1C2B23] text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-[#2B5339] disabled:opacity-50 whitespace-nowrap"
            >
              {digestLoading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <CalendarClock size={12} />
              )}
              Draft Monday batch
            </button>
          </div>

          {digestDrafts.length > 0 && (
            <div data-testid="digest-drafts" className="space-y-3">
              {digestDrafts.map((draft) => {
                const checked = !!digestChecked[draft.source.slug];
                return (
                  <div
                    key={draft.source.slug}
                    data-testid={`digest-draft-${draft.source.slug}`}
                    className={`border rounded-lg p-3 space-y-2 transition-colors ${
                      checked ? "border-[#2B5339] bg-[#F6FAF7]" : "border-[#E5E2DC] bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        data-testid={`digest-check-${draft.source.slug}`}
                        checked={checked}
                        onChange={(e) =>
                          setDigestChecked((m) => ({
                            ...m,
                            [draft.source.slug]: e.target.checked,
                          }))
                        }
                        className="mt-1 h-4 w-4 accent-[#2B5339]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium text-[#1C2B23] truncate">
                            {draft.source.title}
                          </div>
                          <div className="text-[10px] text-[#5C6B62] whitespace-nowrap">
                            {new Date(draft.suggested_at).toLocaleString(undefined, {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                        {draft.variant.image_url && (
                          <a
                            href={draft.variant.image_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block mt-2 rounded-md overflow-hidden border border-[#E5E2DC]"
                          >
                            <img
                              src={draft.variant.image_url}
                              alt=""
                              className="w-full h-auto object-cover"
                              style={{ maxHeight: 90 }}
                            />
                          </a>
                        )}
                        <textarea
                          data-testid={`digest-body-${draft.source.slug}`}
                          value={draft.variant.body}
                          onChange={(e) => updateDigestBody(draft.source.slug, e.target.value)}
                          rows={5}
                          className="mt-2 w-full bg-white border border-[#E5E2DC] rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#2B5339] resize-y"
                        />
                        {draft.variant.hashtags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {draft.variant.hashtags.map((h) => (
                              <span
                                key={h}
                                className="text-[10px] text-[#2B5339] bg-[#E8F0EB] px-1.5 py-0.5 rounded-full"
                              >
                                #{h}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="text-[10px] text-[#5C6B62] mt-1 font-mono-num">
                          {draft.variant.body.length} chars
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="flex items-center justify-between pt-2">
                <div className="text-xs text-[#5C6B62]">
                  {Object.values(digestChecked).filter(Boolean).length} of {digestDrafts.length} selected
                </div>
                <button
                  data-testid="digest-push-btn"
                  type="button"
                  onClick={pushDigestApproved}
                  disabled={digestPushing || !channels.configured}
                  className="inline-flex items-center gap-2 bg-[#2B5339] text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-[#1C2B23] disabled:opacity-50"
                >
                  {digestPushing ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Send size={12} />
                  )}
                  Push approved to Buffer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Autopilot ── */}
        <AutopilotCard pass={pass} channels={channels} />

        {/* ── Weekly digest email ── */}
        <NewsletterCard pass={pass} />

        {variants.length > 0 && (
          <div className="mt-6" data-testid="variants-grid">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-3">
              3. Variants
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {variants.map((v, i) => (
                <VariantCard
                  key={`${v.channel}-${v.tone}-${i}`}
                  v={v}
                  onPublish={openPublish}
                  onCopy={copyVariant}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── History ── */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Recent pushes
            </div>
            <div
              data-testid="total-clicks"
              className="text-[10px] text-[#2B5339] inline-flex items-center gap-1 font-mono-num"
            >
              <MousePointerClick size={10} /> {totalClicks} total clicks
            </div>
          </div>
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-4">
            {history.length === 0 ? (
              <div className="text-xs text-[#5C6B62]">
                Nothing pushed to Buffer yet.
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.map((h) => (
                  <div
                    key={h.id}
                    data-testid={`history-${h.id}`}
                    className="text-xs border-b border-[#F0EFEA] pb-2 last:border-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-[#1C2B23]">
                        {h.channel_service || "buffer"}
                      </span>
                      <span
                        data-testid={`clicks-${h.id}`}
                        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                          (h.click_count || 0) > 0
                            ? "bg-[#E8F0EB] text-[#2B5339]"
                            : "bg-[#F0EFEA] text-[#5C6B62]"
                        }`}
                        title="Tracked clicks on this post's short link"
                      >
                        <MousePointerClick size={9} /> {h.click_count || 0}
                      </span>
                      <span className="text-[10px] text-[#5C6B62]">
                        {h.due_at ? new Date(h.due_at).toLocaleString() : "next slot"} ·{" "}
                        {new Date(h.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-[#5C6B62] mt-1 line-clamp-2 whitespace-pre-wrap">
                      {h.text}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {h.buffer_id && (
                        <div className="text-[10px] text-[#5C6B62] font-mono inline-flex items-center gap-1">
                          <Check size={10} className="text-[#2B5339]" /> {h.buffer_id}
                        </div>
                      )}
                      {h.target_url && (
                        <a
                          href={h.target_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-[#2B5339] hover:underline inline-flex items-center gap-1"
                          title={h.target_url}
                        >
                          <ExternalLink size={9} /> target
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mt-4 text-[11px] text-[#5C6B62]">
            <a
              href="https://publish.buffer.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#2B5339] hover:underline"
            >
              <ExternalLink size={11} /> Open Buffer queue
            </a>
          </div>
        </div>
      </div>

      <PublishDialog
        open={!!dialogVariant}
        variant={dialogVariant}
        channels={channels.channels || []}
        publishing={publishing}
        onClose={() => setDialogVariant(null)}
        onConfirm={confirmPublish}
      />
    </div>
  );
}
